import { useMemo, useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { LinkUsage } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { useEntities } from "../lib/data.ts";
import { buildMap, portalStops } from "../lib/entityMap.ts";
import { NOTATIONS } from "../lib/mapNotations.ts";
import { asTint, DEFAULT_VIEW, isNotation, type MapView } from "../lib/mapView.ts";
import { spaceApi } from "../lib/paths.ts";
import { useRememberedQuery } from "../lib/remember.ts";
import { both, useResource } from "../lib/resource.ts";
import { location, setQuery } from "../lib/router.ts";
import type { PageProps } from "../lib/state.ts";
import { Async, EmptyState } from "../ui/misc.tsx";
import { ItemsNav } from "./items/nav.tsx";
import { BallsMap } from "./map/balls.tsx";
import { CardsMap } from "./map/cards.tsx";
import { mapChoices, mapControls } from "./map/controls.tsx";

const NO_IDENTITY: readonly string[] = [];

/**
 * The entities and how they link, as a diagram to find one's way around (not to edit): a card per entity listing
 * its ref fields, a line from each field to what it links to, ownerships solid and plain links dashed. Hovering a
 * card or a line brings it forward; clicking a card focuses it. It zooms and pans (dragging its empty space).
 */
export function EntityMap({ space }: PageProps) {
  useRememberedQuery(`map.${space.id}`, NO_IDENTITY);
  // The view, in the URL (the defaults left out), remembered like a screen's filters.
  const sp = location.value.searchParams;
  const wanted = sp.get("notation");
  const view: MapView = {
    notation: isNotation(wanted) ? wanted : DEFAULT_VIEW.notation,
    tint: asTint(sp.get("tint")),
    allFields: sp.get("fields") === "all",
  };
  const notation = NOTATIONS[view.notation];
  const onView = (c: Partial<MapView>) =>
    setQuery({
      ...("notation" in c && { notation: c.notation === DEFAULT_VIEW.notation ? null : (c.notation ?? null) }),
      ...("tint" in c && { tint: c.tint === DEFAULT_VIEW.tint ? null : (c.tint ?? null) }),
      ...("allFields" in c && { fields: c.allFields ? "all" : null }),
    });
  const entities = useEntities(space);
  const usage = useResource(`entity-links:${space.id}`, (signal) =>
    api.get<LinkUsage[]>(spaceApi(space, "/entities/links"), undefined, signal),
  );
  const data = both(entities, usage);
  // The map without portals, to measure how linked to each entity is; the threshold (picked, or chosen for you):
  // from 0 (every link a portal) to the most links any entity has (none: no entity is linked from more). Made again
  // only when the entities or their links change, not on every refetch.
  const content = JSON.stringify(data.data ?? null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `content` stands for the data (and the space's entities).
  const base = useMemo(
    () => (data.data ? buildMap(data.data[0], space, data.data[1], new Set()) : { nodes: [], edges: [] }),
    [content],
  );
  const stops = useMemo(() => portalStops(base), [base]);
  const asked = Number.parseInt(location.value.searchParams.get("portals") ?? "", 10);
  const picked = Number.isNaN(asked) ? null : asked;
  const [auto, setAuto] = useState<number | undefined>();
  return (
    <>
      <ItemsNav
        space={space}
        tab="map"
        buttons={mapChoices({ view, onView })}
        actions={mapControls(
          {
            view,
            onView,
            portals: {
              stops,
              value: picked ?? auto,
              auto: picked === null,
              onPick: (x) => setQuery({ portals: x === null ? null : String(x) }),
            },
          },
          false,
        )}
      />
      <Async r={data}>
        {([list, links]) =>
          list.length === 0 ? (
            <EmptyState title={t("map.empty")}>{t("entities.emptyHint")}</EmptyState>
          ) : notation.kind === "balls" ? (
            <BallsMap space={space} map={base} notation={notation} tint={view.tint} />
          ) : (
            <CardsMap
              space={space}
              entities={list}
              usage={links}
              base={base}
              stops={stops}
              notation={notation}
              threshold={picked}
              onAuto={setAuto}
              allFields={view.allFields}
            />
          )
        }
      </Async>
    </>
  );
}
